/**
 * StepStyles - Shared styled components for wizard steps
 *
 * This file contains reusable styled components used across multiple
 * wizard step components to ensure consistent styling and reduce duplication.
 */

import styled from 'styled-components';

/**
 * Phase status type used for styling phase indicators
 */
export type PhaseStatus = 'pending' | 'active' | 'complete';

/**
 * Container for step content with consistent gap
 */
export const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/**
 * Container for phase list with consistent styling
 */
export const PhaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: var(--color-surface-1);
  border-radius: 10px;
`;

/**
 * Individual phase item with status-based opacity
 */
export const PhaseItem = styled.div<{ $status: PhaseStatus }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  opacity: ${(props: { $status: PhaseStatus }): number =>
    props.$status === 'pending' ? 0.5 : 1};
  transition: opacity 300ms ease;
`;

/**
 * Phase icon with status-based background color
 */
export const PhaseIcon = styled.div<{ $status: PhaseStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props: { $status: PhaseStatus }): string => {
    if (props.$status === 'complete') return '#66bb6a';
    if (props.$status === 'active') return 'var(--color-primary)';
    return 'var(--color-surface-2)';
  }};
  transition: background 300ms ease;
  flex-shrink: 0;
  margin-top: 2px;
`;

/**
 * Phase label text
 */
export const PhaseLabel = styled.span`
  font-size: 14px;
  color: var(--color-text);
`;

/**
 * Container for phase content (label + hint)
 */
export const PhaseContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/**
 * Phase hint/description text
 */
export const PhaseHint = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
`;

/**
 * Info section with icon and text
 */
export const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: var(--color-surface-1);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.72);
`;

/**
 * Info text within InfoSection
 */
export const InfoText = styled.span`
  font-size: 13px;
  line-height: 1.5;
`;

/**
 * Utility function to get phase status based on current phase
 *
 * @param phases - Array of phase names in order
 * @param currentPhase - The current active phase
 * @param targetPhase - The phase to get status for
 * @returns The status of the target phase
 */
export const getPhaseStatus = <T extends string>(
  phases: T[],
  currentPhase: T,
  targetPhase: T
): PhaseStatus => {
  const currentIndex = phases.indexOf(currentPhase);
  const targetIndex = phases.indexOf(targetPhase);

  if (targetIndex < currentIndex) return 'complete';
  if (targetIndex === currentIndex) return 'active';
  return 'pending';
};
